import { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from 'i18n/config';
import { translate } from 'hooks/useTranslate';

import { TradingOfferOperationStatus } from 'types/TradingOfferOperation';
import { SelectorOption } from 'types';
import { TradingOfferOperationsCount } from 'types/Society';

const statusOptionsRaw: {
  value: string;
  label: string;
  countKey: keyof TradingOfferOperationsCount;
}[] = [
  {
    value: TradingOfferOperationStatus.PENDING,
    label: 'Pending',
    countKey: 'pending',
  },
  {
    value: TradingOfferOperationStatus.KYC_REVIEW,
    label: 'KYCReview',
    countKey: 'kycReview',
  },
  {
    value: TradingOfferOperationStatus.VALIDATION,
    label: 'PendingValidation',
    countKey: 'validation',
  },
  {
    value: TradingOfferOperationStatus.PAYMENT_PENDING,
    label: 'PaymentPending',
    countKey: 'paymentPending',
  },
  {
    value: TradingOfferOperationStatus.TRANSFER_PENDING,
    label: 'TransferPending',
    countKey: 'transferPending',
  },
  {
    value: TradingOfferOperationStatus.COMPLETED,
    label: 'Completed',
    countKey: 'completed',
  },
  {
    value: TradingOfferOperationStatus.CANCELLED,
    label: 'Cancelled',
    countKey: 'cancelled',
  },
  {
    value: TradingOfferOperationStatus.REJECTED,
    label: 'Rejected',
    countKey: 'rejected',
  },
];

type UseGetTradingOfferOperationStatusOptionsProps = {
  tradingOfferOperationsCount?: TradingOfferOperationsCount;
  showAllOptions?: boolean;
  tradingOfferOperationStatus?: TradingOfferOperationStatus | string | null;
};

export const useGetTradingOfferOperationStatusOptions = ({
  tradingOfferOperationsCount,
  showAllOptions = false,
  tradingOfferOperationStatus,
}: UseGetTradingOfferOperationStatusOptionsProps) => {
  const [language, setLanguage] = useState(i18n.language);
  const [selectedStatusOption, setSelectedStatusOption] =
    useState<SelectorOption | null>(null);

  useEffect(() => {
    const handleLanguageChanged = (lng: string) => setLanguage(lng);
    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  const mapOption = useCallback(
    (option: { value: string; label: string }): SelectorOption => ({
      value: option.value,
      label: translate(option.label),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  );

  const statusOptions = useMemo(
    () =>
      statusOptionsRaw
        .filter(
          (option) =>
            showAllOptions ||
            (tradingOfferOperationsCount?.[option.countKey] ?? 0) > 0
        )
        .map(mapOption),
    [mapOption, tradingOfferOperationsCount, showAllOptions]
  );

  useEffect(() => {
    if (!tradingOfferOperationStatus) {
      setSelectedStatusOption(null);
      return;
    }

    const matchingRawOption = statusOptionsRaw.find(
      (option) => option.value === tradingOfferOperationStatus
    );

    setSelectedStatusOption(
      matchingRawOption ? mapOption(matchingRawOption) : null
    );
  }, [tradingOfferOperationStatus, mapOption]);

  const handleChangeOption = (selectedOption: SelectorOption | null) => {
    setSelectedStatusOption(selectedOption);
  };

  return {
    data: {
      statusOptions,
      selectedStatusOption,
    },
    actions: {
      handleChangeOption,
    },
  };
};
